import {
  cos,
  dot,
  float,
  floor,
  int,
  length,
  mod,
  normalize,
  sin,
  tan,
  uniform,
  uniformArray,
  vec3,
} from 'three/tsl';
import { Vector3 } from 'three/webgpu';
import { dirToFace, FACE_N, FACE_U, FACE_V, type FaceCoord, faceToDir } from './cubeSphere';

type N = any;

/** A root tile and a position on it, (u, v) ∈ [−1, 1]². */
export interface RootCoord {
  root: number;
  u: number;
  v: number;
}

/**
 * The surface a hollow's terrain lies on: a sphere's inside wall, or a torus's. Terrain is
 * a quadtree over root tiles, each a (u, v) ∈ [−1, 1]² patch of the surface. Heights move
 * a point from the reference surface into the void, along −normal.
 *
 * Everything is relative to the hollow's centre.
 */
export interface TerrainShape {
  readonly kind: 'sphere' | 'torus';
  readonly rootCount: number;
  /** Edge length of a root tile (m), the largest anywhere; nodes halve it per level. */
  readonly rootArc: number;
  /** Point on the reference surface (height 0). */
  point(root: number, u: number, v: number, out: Vector3): Vector3;
  /** Unit normal pointing out of the void, into the rock. */
  normal(root: number, u: number, v: number, out: Vector3): Vector3;
  /** Unit tangents along u and v. */
  tangents(root: number, u: number, v: number, outU: Vector3, outV: Vector3): void;
  /** The root tile and (u, v) under a point. */
  locate(p: Vector3, out: RootCoord): RootCoord;
  /** How far a point is from the reference surface into the void (negative in the rock). */
  heightAbove(p: Vector3): number;
  /** The same mapping on the GPU. */
  readonly gpu: ShapeNodes;
}

const _rcS: RootCoord = { root: 0, u: 0, v: 0 };

/** The reference-surface point under a hollow-local point (its "horizontal" position). */
export function surfacePoint(shape: TerrainShape, p: Vector3, out: Vector3): Vector3 {
  shape.locate(p, _rcS);
  return shape.point(_rcS.root, _rcS.u, _rcS.v, out);
}

/** A tile vertex on the GPU: where it is and which way the surface faces. */
export interface SurfaceNodes {
  /** Reference-surface point relative to the tile centre's (m). */
  offset: N;
  /**
   * The same point relative to the hollow's centre, for colours and noise. It's large, so in
   * f32 it is only as exact as the hollow is small; positions use `offset`.
   */
  local: N;
  /** Unit normal into the rock (heights move along its negative). */
  normal: N;
  /** Axes the tiles store gradients along (U, V), and N with U × V ≈ N. */
  U: N;
  V: N;
  N: N;
}

export interface ShapeNodes {
  /**
   * Surface at grid position g ∈ [0, 1]² of the tile centred on (uc, vc) with edge `size`
   * in root `root`. Offsets are computed without cancellation, so they stay exact near
   * the camera however large the hollow.
   */
  at(root: N, uc: N, vc: N, size: N, g: N): SurfaceNodes;
}

const QUARTER_PI = Math.PI / 4;
const _fc: FaceCoord = { face: 0, u: 0, v: 0 };
const _d = new Vector3();

/** The inside of a sphere: six cube-sphere faces (see cubeSphere.ts). */
export class SphereShape implements TerrainShape {
  readonly kind = 'sphere' as const;
  readonly rootCount = 6;
  readonly rootArc: number;
  readonly gpu: ShapeNodes;

  constructor(readonly radius: number) {
    this.rootArc = (Math.PI / 2) * radius;
    const R = uniform(radius);
    const axes: N = uniformArray(
      FACE_N.flatMap((n, f) => [n.clone(), FACE_U[f].clone(), FACE_V[f].clone()]),
      'vec3',
    );
    // Cancellation-free tan(a+d) − tan(a) = sin(d) / (cos(a) cos(a+d)).
    const tanDelta = (a: N, d: N) =>
      sin(d.mul(QUARTER_PI)).div(cos(a.mul(QUARTER_PI)).mul(cos(a.add(d).mul(QUARTER_PI))));
    this.gpu = {
      at(root, uc, vc, size, g) {
        const fi = int(root.add(0.5));
        const Nf = axes.element(fi.mul(3));
        const Uf = axes.element(fi.mul(3).add(1));
        const Vf = axes.element(fi.mul(3).add(2));
        const du = g.x.sub(0.5).mul(size);
        const dv = g.y.sub(0.5).mul(size);
        const qc = Nf.add(Uf.mul(tan(uc.mul(QUARTER_PI)))).add(Vf.mul(tan(vc.mul(QUARTER_PI))));
        const dq = Uf.mul(tanDelta(uc, du)).add(Vf.mul(tanDelta(vc, dv)));
        const L0 = length(qc);
        const L1 = length(qc.add(dq));
        // normalize(qc + dq) − normalize(qc), without subtracting nearly equal numbers.
        const dn = dq
          .div(L1)
          .sub(qc.mul(dot(qc, dq).mul(2).add(dot(dq, dq)).div(L0.add(L1).mul(L0).mul(L1))));
        const dir = normalize(qc.div(L0).add(dn));
        return { offset: dn.mul(R), local: dir.mul(R), normal: dir, U: Uf, V: Vf, N: Nf };
      },
    };
  }

  point(root: number, u: number, v: number, out: Vector3): Vector3 {
    return faceToDir(root, u, v, out).multiplyScalar(this.radius);
  }

  normal(root: number, u: number, v: number, out: Vector3): Vector3 {
    return faceToDir(root, u, v, out);
  }

  tangents(root: number, u: number, v: number, outU: Vector3, outV: Vector3): void {
    const d = faceToDir(root, u, v, _d);
    const helper = Math.abs(d.y) < 0.9 ? outV.set(0, 1, 0) : outV.set(1, 0, 0);
    outU.crossVectors(helper, d).normalize();
    outV.crossVectors(d, outU).normalize();
  }

  locate(p: Vector3, out: RootCoord): RootCoord {
    dirToFace(_d.copy(p).normalize(), _fc);
    out.root = _fc.face;
    out.u = _fc.u;
    out.v = _fc.v;
    return out;
  }

  heightAbove(p: Vector3): number {
    return this.radius - p.length();
  }
}

/**
 * The inside of a torus: a tube of radius `tube` whose centre line is a circle of radius
 * `ring` around the y axis. Angle θ runs around the ring (from +x toward −z) and φ around
 * the tube (0 at the outer equator, π/2 on top). Roots are an nθ × nφ grid of (θ, φ)
 * patches; both angles wrap, so the grid has no edges. θ turns that way so that u × v
 * points into the rock, as on a sphere's faces: tile triangles are wound by it, and the
 * other way round they would face the rock and be culled.
 */
export class TorusShape implements TerrainShape {
  readonly kind = 'torus' as const;
  readonly rootCount: number;
  readonly rootArc: number;
  readonly gpu: ShapeNodes;
  private readonly dTheta: number;
  private readonly dPhi: number;

  constructor(
    readonly ring: number,
    readonly tube: number,
    readonly nTheta = 12,
    readonly nPhi = 4,
  ) {
    this.rootCount = nTheta * nPhi;
    this.dTheta = (2 * Math.PI) / nTheta;
    this.dPhi = (2 * Math.PI) / nPhi;
    // Tiles on the outer equator are the widest.
    this.rootArc = Math.max(this.dTheta * (ring + tube), this.dPhi * tube);
    const Rr = uniform(ring);
    const r = uniform(tube);
    const dT = this.dTheta;
    const dP = this.dPhi;
    this.gpu = {
      at(root, uc, vc, size, g) {
        const i = mod(root, float(nTheta)).floor();
        const j = floor(root.add(0.5).div(nTheta));
        const u = uc.add(g.x.sub(0.5).mul(size));
        const v = vc.add(g.y.sub(0.5).mul(size));
        const th = i.add(u.add(1).mul(0.5)).mul(dT);
        const ph = j.add(v.add(1).mul(0.5)).mul(dP);
        const thc = i.add(uc.add(1).mul(0.5)).mul(dT);
        const phc = j.add(vc.add(1).mul(0.5)).mul(dP);
        // Differences of sines and cosines as products, so small offsets stay exact.
        // θ − θc = (u − uc)/2 · dθ, and the identities want half of that.
        const hdT = g.x
          .sub(0.5)
          .mul(size)
          .mul(dT * 0.25);
        const hdP = g.y
          .sub(0.5)
          .mul(size)
          .mul(dP * 0.25);
        const mT = th.add(thc).mul(0.5);
        const mP = ph.add(phc).mul(0.5);
        const dCosT = sin(mT).mul(sin(hdT)).mul(-2);
        const dSinT = cos(mT).mul(sin(hdT)).mul(2);
        const dCosP = sin(mP).mul(sin(hdP)).mul(-2);
        const dSinP = cos(mP).mul(sin(hdP)).mul(2);
        const rhoC = Rr.add(r.mul(cos(phc)));
        const dRho = r.mul(dCosP);
        const cosT = cos(th);
        const sinT = sin(th);
        const offset = vec3(
          dRho.mul(cosT).add(rhoC.mul(dCosT)),
          r.mul(dSinP),
          dRho.mul(sinT).add(rhoC.mul(dSinT)).negate(),
        );
        const cosP = cos(ph);
        const sinP = sin(ph);
        const normal = vec3(cosP.mul(cosT), sinP, cosP.mul(sinT).negate());
        const rho = Rr.add(r.mul(cosP));
        const local = vec3(rho.mul(cosT), r.mul(sinP), rho.mul(sinT).negate());
        const U = vec3(sinT.negate(), 0, cosT.negate());
        const V = vec3(sinP.negate().mul(cosT), cosP, sinP.mul(sinT));
        return { offset, local, normal, U, V, N: normal };
      },
    };
  }

  private angles(root: number, u: number, v: number): [number, number] {
    const i = root % this.nTheta;
    const j = Math.floor(root / this.nTheta);
    return [(i + (u + 1) / 2) * this.dTheta, (j + (v + 1) / 2) * this.dPhi];
  }

  point(root: number, u: number, v: number, out: Vector3): Vector3 {
    const [th, ph] = this.angles(root, u, v);
    const rho = this.ring + this.tube * Math.cos(ph);
    return out.set(rho * Math.cos(th), this.tube * Math.sin(ph), -rho * Math.sin(th));
  }

  normal(root: number, u: number, v: number, out: Vector3): Vector3 {
    const [th, ph] = this.angles(root, u, v);
    return out.set(Math.cos(ph) * Math.cos(th), Math.sin(ph), -Math.cos(ph) * Math.sin(th));
  }

  tangents(root: number, u: number, v: number, outU: Vector3, outV: Vector3): void {
    const [th, ph] = this.angles(root, u, v);
    outU.set(-Math.sin(th), 0, -Math.cos(th));
    outV.set(-Math.sin(ph) * Math.cos(th), Math.cos(ph), Math.sin(ph) * Math.sin(th));
  }

  locate(p: Vector3, out: RootCoord): RootCoord {
    const tau = 2 * Math.PI;
    const th = (Math.atan2(-p.z, p.x) + tau) % tau;
    const ph = (Math.atan2(p.y, Math.hypot(p.x, p.z) - this.ring) + tau) % tau;
    const i = Math.min(this.nTheta - 1, Math.floor(th / this.dTheta));
    const j = Math.min(this.nPhi - 1, Math.floor(ph / this.dPhi));
    out.root = j * this.nTheta + i;
    out.u = Math.max(-1, Math.min(1, ((th - i * this.dTheta) / this.dTheta) * 2 - 1));
    out.v = Math.max(-1, Math.min(1, ((ph - j * this.dPhi) / this.dPhi) * 2 - 1));
    return out;
  }

  heightAbove(p: Vector3): number {
    return this.tube - Math.hypot(Math.hypot(p.x, p.z) - this.ring, p.y);
  }
}
